<template>
  <div class="discovery-container">
    <div class="search-bar-wrapper">
      <div class="search-bar">
        <!-- Dynamic Icon based on search mode -->
        <div class="search-icon" @click="search">
          <i :class="isAIQuery ? 'fa fa-comment-dots ai-icon-color' : 'fa fa-search'"></i>
        </div>
        
        <!-- Smart dynamic placeholder -->
        <input
          type="text"
          class="search-input"
          :placeholder="isAIQuery ? 'Ask AI a question...' : 'Search keywords or ask a question...'"
          v-model="searchText"
          @keyup.enter="search"
        />

        <!-- Clear Search button -->
        <div v-if="searchText" class="clear-icon" @click="clearSearch">
          <i class="fa fa-times"></i>
        </div>

        <!-- Mode Indicator / Dropdown Selector -->
        <div class="mode-selector">
          <select v-model="searchMode" class="mode-dropdown">
            <option value="auto">Auto</option>
            <option value="search">Keyword</option>
            <option value="ai">AI Chat</option>
          </select>
        </div>

        <!-- Action Button that changes contextually -->
        <button class="action-btn" :class="{ 'ai-btn': isAIQuery }" @click="search">
          {{ isAIQuery ? 'Ask AI' : 'Search' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useStore } from "vuex";
import { ref, watch, onMounted, computed } from "vue";

const searchText = ref('');
const searchMode = ref('auto'); // Options: 'auto', 'search', 'ai'
const store = useStore();

// Heuristic rule to detect if user intent is an AI question/prompt
const isAIQuery = computed(() => {
  // If explicitly forced by dropdown selector
  if (searchMode.value === 'search') return false;
  if (searchMode.value === 'ai') return true;

  const text = searchText.value.trim().toLowerCase();
  if (!text) return false;

  // Rule 1: Contains a question mark
  if (text.includes("?")) return true;

  // Rule 2: Exceeds a natural keyword length threshold
  if (text.length > 45) return true;

  // Rule 3: Starts with conversational LLM prefixes
  const aiPrefixes = [
    "who", "what", "where", "when", "why", "how", 
    "summarise", "summarize", "compare", "show me", 
    "find all", "tell me", "explain", "draft"
  ];

  return aiPrefixes.some(prefix => text.startsWith(prefix));
});

onMounted(() => {
  const storeSearchText = store.getters.searchText;
  if (storeSearchText) {
    searchText.value = storeSearchText;
    search();
  }
});

const search = () => {
  const query = searchText.value.trim();
  if (!query) return;

  console.log(`🚀 Routing Discovery Term: "${query}" | AI Mode Active: ${isAIQuery.value}`);

  if (isAIQuery.value) {
    // Calls your brand new LLM / AI conversational endpoint
    store.dispatch("fetchAIQueryResults", query);
  } else {
    // Calls your existing keyword string match endpoint 
    store.dispatch("fetchSearchResults", query);
  }
};

const clearSearch = () => {
  searchText.value = '';
  store.commit("setSearchText", ""); 
  store.commit("clearSearchResults");
  store.commit("nodeIdSelected", "0");
  store.dispatch("emailKnowledgeBaseNode");
};

const clearSearchResults = () => {
  store.commit("clearSearchResults");
};

watch(searchText, (newValue) => {
  if (newValue === '') {
    clearSearchResults();
  }
});

// Expose the clearSearch function so parent components can continue calling it flawlessly
defineExpose({
  clearSearch
});
</script>

<style scoped>
.discovery-container {
  display: flex;
  width: 100%;
}

.search-bar-wrapper {
  width: 100%;
  max-width: 650px; /* Slightly wider to accommodate button and mode toggle naturally */
}

.search-bar {
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 4px 8px 4px 12px; /* Tightened vertical padding to allow buttons to look embedded */
  transition: all 0.3s ease;
  width: 100%; 
  background-color: #fff;
}

.search-bar:hover, .search-bar:focus-within {
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-color: #a0a0a0;
}

.search-icon {
  margin-right: 10px;
  cursor: pointer;
  font-size: 16px;
  color: #666;
}

.ai-icon-color {
  color: #007bff; /* Clear hint that AI mode has kicked in */
}

.search-input {
  border: none;
  outline: none;
  flex-grow: 1;
  font-size: 14px;
  padding: 6px 0;
}

.clear-icon {
  cursor: pointer;
  margin-left: 8px;
  margin-right: 8px;
  color: #888;
  transition: color 0.2s ease;
}

.clear-icon:hover {
  color: #333;
}

/* Embedded Dropdown Override styling */
.mode-selector {
  border-left: 1px solid #eee;
  padding-left: 8px;
  margin-right: 8px;
}

.mode-dropdown {
  border: none;
  background: transparent;
  outline: none;
  font-size: 12px;
  color: #666;
  cursor: pointer;
  padding: 4px;
}

/* Action Trigger Button */
.action-btn {
  background-color: #333;
  color: #fff;
  border: none;
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.action-btn:hover {
  background-color: #111;
}

.action-btn.ai-btn {
  background-color: #007bff;
}

.action-btn.ai-btn:hover {
  background-color: #0056b3;
}
</style>